import {ChangeDetectionStrategy, Component, signal, OnInit} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  ActivatedRoute,
  Event as NavigationEvent,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
  RouterOutlet
} from '@angular/router';
import {NbEvaIconsModule} from '@nebular/eva-icons';
import {
  NbActionsModule,
  NbButtonModule,
  NbCardModule,
  NbContextMenuModule,
  NbDialogService,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbMenuItem,
  NbMenuModule,
  NbMenuService,
  NbPopoverModule,
  NbSidebarModule,
  NbSidebarService
} from '@nebular/theme';
import {StorageService} from './services/storage.service';
import {AuthService} from './services/auth.service';
import {LocaleService} from './services/locale.service';
import {LanguageSwitcherComponent} from './shared/components/language-switcher/language-switcher.component';
import {NgxPermissionsModule, NgxPermissionsService} from 'ngx-permissions';
import {Subject} from "rxjs";
import {AccountInfosFormComponent} from "./pages/auth/account-infos-form/account-infos-form.component";

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet, RouterLink,
    NbLayoutModule, NbSidebarModule, NbButtonModule, NbCardModule, NbMenuModule, NbEvaIconsModule, NbIconModule, NbInputModule, NbFormFieldModule,
    NgxPermissionsModule, NbPopoverModule, NbContextMenuModule, NbActionsModule,
    LanguageSwitcherComponent,
    TranslateModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private destroy$ = new Subject<void>();
  title = 'ig-frontend';

  // activeItem: any;
  itemsArray: any = [];
  items = signal<NbMenuItem[]>([]);

  sideBarIcon = 'menu-outline';
  isLoggedIn = false;
  isMobileDisplay = false;
  isTournamentMode = false;
  isErrorPage = false;
  isTournamentBackoffice = false;
  isBackoffice = false;
  event$: any;
  currentUrl = '';
  entityCode = '';
  currentYear: number = new Date().getFullYear();
  protected user: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private permissionsService: NgxPermissionsService,
    private storageService: StorageService,
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private authService: AuthService,
    private dialogService: NbDialogService,
    private localeService: LocaleService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.localeService.syncLocale();
    this.user = this.storageService.user();
    const permissions = this.user?.permissions;
    if (permissions) {
      this.permissionsService.loadPermissions(permissions);
    }
    this.isLoggedIn = this.storageService.isLoggedIn();
    this.checkBackofficeContext();
    this.menuService.onItemClick().subscribe((menuBag: any) => {
      if (menuBag.item?.link) {
        this.authService.setRedirectUrl(menuBag.item.link);
        if (this.isMobileDisplay) this.sidebarService.collapse();
        this.currentUrl = menuBag.item.link;
        this.items.update(items =>
          items.map(item => ({ ...item, selected: item.link === menuBag.item.link }))
        );
      }
    });

    this.translate.onLangChange.subscribe(() => {
      if (this.isTournamentMode) {
        this.setTournamentAppRoutes();
      } else {
        this.setMainAppRoutes();
      }
      this.applyPermissionsAndSetMenu();
    });
  }

  ngOnDestroy() {
    this.event$.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  checkBackofficeContext(): void {
    this.event$ = this.router.events.subscribe((event: NavigationEvent) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = this.getDeepestChild(this.route);
        const data = currentRoute.routeConfig?.data;
        this.isErrorPage = data?.['isErrorPage'] === true;
        setTimeout(() => {
          if (this.isErrorPage) {
            this.itemsArray = [];
            this.items.set(this.itemsArray);
          }
        }, 1000);
      }
      else if (event instanceof NavigationStart) {
        this.currentUrl = event.url;
        this.isTournamentMode = this.currentUrl.startsWith('/tourney');
        this.isBackoffice = this.currentUrl.startsWith('/admin');
        this.isTournamentBackoffice = false;

        if (this.isTournamentMode) {
          const segments = this.currentUrl.split('/');
          this.entityCode = segments[2];
          this.isTournamentBackoffice = this.currentUrl.startsWith(`/tourney/${this.entityCode}/admin`);
          this.setTournamentAppRoutes();
        }
        else {
          this.setMainAppRoutes();
        }

        this.applyPermissionsAndSetMenu();
      }
    });
  }

  onResponsiveStateChange(e: any) {
    this.isMobileDisplay = (e == 'mobile');
  }

  toggle() {
    this.sidebarService.toggle(false);
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.storageService.clean();
        window.location.assign('/');
      },
      error: err => {
        // console.log(err);
      }
    });
  }

  tournamentLogout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.storageService.clean();
        window.location.assign(`/tourney/${this.entityCode}`);
      },
      error: err => {
        // console.log(err);
      }
    });
  }

  private applyPermissionsAndSetMenu(): void {
    const permissionChecks = this.itemsArray.map((item: any) =>
      this.permissionsService.hasPermission(item.only).then((hasPermission) => ({item, hasPermission}))
    );
    Promise.all(permissionChecks).then((results) => {
      const filteredItems = results.filter(r => r.hasPermission).map(r => r.item);
      const sortedItems = filteredItems.sort((a: any, b: any) => {
        const aHasOrder = a.order !== undefined;
        const bHasOrder = b.order !== undefined;
        if (!aHasOrder && !bHasOrder) return 0;
        if (!aHasOrder) return -1;
        if (!bHasOrder) return 1;
        return a.order - b.order;
      });
      const currentUrl = this.currentUrl;
      sortedItems.forEach(item => {
        item.selected = !!item.link && (currentUrl === item.link || currentUrl.startsWith(item.link + '/'));
      });
      this.items.set(sortedItems);
    });
  }

  setMainAppRoutes() {
    this.itemsArray = [
      {
        title: this.translate.instant('nav.home'),
        link: '/home',
      }
    ];

    if (this.isLoggedIn) {
      if (this.isBackoffice) {
        this.setMainAppBackofficeRoutes();
      }
      else {
        this.setMainAppLoggedInRoutes();
      }
    }
    else {
      this.setMainAppPublicAccessRoutes();
    }
  }

  setTournamentAppRoutes() {
    this.itemsArray = [
      {
        title: this.translate.instant('nav.home'),
        link: `/tourney/${this.entityCode}/home`,
      }
    ];

    if (this.isLoggedIn) {
      if (this.isTournamentBackoffice) {
        this.setTournamentBackofficeRoutes();
      }
      else {
        this.setTournamentAppLoggedInRoutes();
      }
    }
    else {
      this.setTournamentAppPublicAccessRoutes();
    }
  }

  setMainAppBackofficeRoutes() {
    this.itemsArray = this.itemsArray.concat([
      {
        title: this.translate.instant('nav.bo_live_games'),
        link: '/admin/live-games',
        only: ['BO-LIVE-GAMES'],
      },
      {
        title: this.translate.instant('nav.bo_database'),
        link: '/admin/database',
        only: ['BO-DATABASE']
      },
      {
        title: this.translate.instant('nav.bo_divisions'),
        link: '/admin/divisions',
        only: ['BO-DIVISIONS']
      },
      {
        title: this.translate.instant('nav.bo_payments'),
        link: '/admin/payments',
        only: ['BO-PAYMENTS']
      },
      {
        title: this.translate.instant('nav.bo_groups'),
        link: '/admin/groups',
        only: ['BO-GROUPS'],
      },
      {
        title: this.translate.instant('nav.bo_standings'),
        link: '/admin/standings',
        only: ['BO-STANDINGS']
      },
      {
        title: this.translate.instant('nav.bo_games'),
        link: '/admin/games',
        only: ['BO-GAMES'],
      },
      {
        title: this.translate.instant('nav.bo_playoff_pools'),
        link: '/admin/playoff-pools',
        only: ['BO-PLAYOFFS'],
      },
      {
        title: this.translate.instant('nav.bo_playoffs'),
        link: '/admin/bracket-sessions',
        only: ['BO-PLAYOFFS'],
      },
      {
        title: this.translate.instant('nav.bo_schedule'),
        link: '/admin/schedule',
        only: ['BO-SCHEDULE'],
      },
      {
        title: this.translate.instant('nav.bo_transfers'),
        link: '/admin/transfers',
        only: ['BO-TRANSFERS'],
      },
      {
        title: this.translate.instant('nav.bo_users'),
        link: '/admin/users',
        only: ['BO-USERS'],
      },
      {
        title: this.translate.instant('nav.bo_players'),
        link: '/admin/players',
        only: ['BO-PLAYERS'],
      },
    ]);
  }

  setTournamentBackofficeRoutes() {
    this.itemsArray = this.itemsArray.concat([
      {
        title: this.translate.instant('nav.bo_teams'),
        link: `/tourney/${this.entityCode}/admin/teams`,
        only: ['BO-TOURNAMENT-TEAMS']
      },
      {
        title: this.translate.instant('nav.bo_results'),
        link: `/tourney/${this.entityCode}/admin/results`,
        only: ['BO-TOURNAMENT-RESULTS']
      },
    ]);
  }

  setMainAppLoggedInRoutes() {
    this.setMainAppPublicAccessRoutes();
    this.itemsArray = this.itemsArray.concat([
      // {
      //   title: this.translate.instant('nav.transfers'),
      //   link: '/transfers',
      //   only: ['TRANSFERS'],
      // },
      {
        title: this.translate.instant('nav.invite_members'),
        link: '/invite-teammates',
        only: ['INVITE_MEMBERS'],
      },
      {
        title: this.translate.instant('nav.backoffice'),
        link: '/admin',
        only: ['BO-ACCESS'],
        order: 1000
      },
    ]);
  }

  setTournamentAppLoggedInRoutes() {
    this.setTournamentAppPublicAccessRoutes();
    this.itemsArray = this.itemsArray.concat([
      {
        title: this.translate.instant('nav.backoffice'),
        link: `/tourney/${this.entityCode}/admin`,
        only: ['BO-TOURNAMENT']
      },
    ]);
  }

  private setMainAppPublicAccessRoutes() {
    this.itemsArray = this.itemsArray.concat(this.mainAppPublicAccessRoutes());
  }

  private setTournamentAppPublicAccessRoutes() {
    this.itemsArray = this.itemsArray.concat(this.tournamentAppPublicAccessRoutes());
  }

  private mainAppPublicAccessRoutes() {
    return [
      {
        title: this.translate.instant('nav.live_games'),
        link: '/live-games',
      },
      {
        title: this.translate.instant('nav.playoffs'),
        link: '/playoffs'
      },
      {
        title: this.translate.instant('nav.standings'),
        link: '/standings'
      },
      // {
      //   title: this.translate.instant('nav.teams'),
      //   link: '/teams',
      // },
      // {
      //   title: this.translate.instant('nav.games'),
      //   link: '/games',
      // },
      {
        title: this.translate.instant('nav.support'),
        link: '/support/videos',
        order: 90,
      },
    ];
  }

  private tournamentAppPublicAccessRoutes() {
    return [
      // {
      //   title: 'Bracket',
      //   link: `/tourney/${this.entityCode}/bracket`,
      // },
      // {
      //   title: 'Matchs',
      //   link: `/tourney/${this.entityCode}/matches`,
      // },
      // {
      //   title: 'Teams',
      //   link: `/tourney/${this.entityCode}/teams`,
      // },
      // {
      //   title: 'Rules',
      //   link: `/tourney/${this.entityCode}/rules`,
      // },
    ]
  }

  protected editAccountActivision() {
    this.dialogService.open(AccountInfosFormComponent, {
      context: {
        user: this.user,
      },
    });
  }
}

